import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import type { TrackingInteractiveQuestionType } from '@genially/contracts';
import { InteractiveQuestionType } from '@genially/document-model';
import { PlayerPlayP2pClientEventType } from '@genially/p2p-client';
import { isInteractiveQuestionItemData } from '@genially/ui/build/components/SidebarSummary/utils/isInteractiveQuestion';

import { PlaySidebarSummary } from '../../../../../../../../shared/application/components/PlaySidebarSummary/PlaySidebarSummary';
import { usePlayerClientContext } from '../../../PlayerClientProvider/hooks/usePlayerClientContext';
import { usePlayerGameContext } from '../../PlayerGame.context';
import { WaitingToResolveAnimation } from './components/WaitingToResolveAnimation/WaitingToResolveAnimation';
import {
  type AnsweredWidgetTracking,
  decideSidebarBehaviourOnAnswer,
  SidebarBehaviourOnAnswer,
} from './decideSidebarBehaviourOnAnswer';
import { Wrapper } from './PlayerPlaySidebarSummary.styled';

export const PlayerPlaySidebarSummary = observer(() => {
  const { client } = usePlayerClientContext();
  const { viewConnection, sidebarSummary, interactiveQuestions, rankingEnabled } =
    usePlayerGameContext();

  const [showWaitingToResolveAnimation, setShowWaitingToResolveAnimation] =
    useState(false);

  const handleOpenSidebarEvent = useCallback(
    async ({
      interactiveQuestionId,
      isLastScored,
    }: {
      interactiveQuestionId: string;
      isLastScored?: boolean;
    }) => {
      if (!viewConnection.isConnected) return;

      const interactiveQuestion = await interactiveQuestions.find(
        i => i.Id === interactiveQuestionId,
      );

      if (
        !interactiveQuestion ||
        (isInteractiveQuestionItemData(interactiveQuestion) &&
          interactiveQuestion.Type === InteractiveQuestionType.BranchingScenario)
      ) {
        return;
      }

      if (
        sidebarSummary.selectedQuestion?.interactiveQuestion.Id === interactiveQuestion.Id
      ) {
        return;
      }

      sidebarSummary.setSelectedQuestion({
        interactiveQuestion,
        isLastScored: isLastScored ?? false,
      });
      sidebarSummary.open();

      setShowWaitingToResolveAnimation(false);
    },
    [interactiveQuestions, sidebarSummary, viewConnection.isConnected],
  );

  // Tracking flags of an answered widget, or undefined when the answered question is a native
  // one. They decide whether its results are shown grouped (isCategorizable, like a survey) or
  // after the host resolves it (isScoreable) — see decideSidebarBehaviourOnAnswer.
  const findAnsweredWidgetTracking = useCallback(
    (interactiveQuestionId: string): AnsweredWidgetTracking | undefined => {
      const interactiveQuestion = interactiveQuestions.find(
        i => i.Id === interactiveQuestionId,
      );

      if (!interactiveQuestion || isInteractiveQuestionItemData(interactiveQuestion)) {
        return undefined;
      }

      return interactiveQuestion.script?.tracking;
    },
    [interactiveQuestions],
  );

  const handleInteractiveQuestionAnswered = useCallback(
    ({
      interactiveQuestionId,
      type,
      isEmptyAnswer,
    }: {
      interactiveQuestionId: string;
      type: TrackingInteractiveQuestionType;
      isEmptyAnswer?: boolean;
    }) => {
      const behaviour = decideSidebarBehaviourOnAnswer({
        type,
        isEmptyAnswer,
        widgetTracking: findAnsweredWidgetTracking(interactiveQuestionId),
      });

      if (behaviour === SidebarBehaviourOnAnswer.OpenSummary) {
        handleOpenSidebarEvent({ interactiveQuestionId });
        return;
      }

      if (behaviour === SidebarBehaviourOnAnswer.WaitForHost) {
        setShowWaitingToResolveAnimation(true);
        sidebarSummary.open();
      }
    },
    [findAnsweredWidgetTracking, handleOpenSidebarEvent, sidebarSummary],
  );

  useEffect(() => {
    client.on(
      PlayerPlayP2pClientEventType.InteractiveQuestionResolved,
      handleOpenSidebarEvent,
    );

    return () => {
      client.off(
        PlayerPlayP2pClientEventType.InteractiveQuestionResolved,
        handleOpenSidebarEvent,
      );
    };
  }, [client, handleOpenSidebarEvent]);

  useEffect(() => {
    client.on(
      PlayerPlayP2pClientEventType.InteractiveQuestionAnswered,
      handleInteractiveQuestionAnswered,
    );

    return () => {
      client.off(
        PlayerPlayP2pClientEventType.InteractiveQuestionAnswered,
        handleInteractiveQuestionAnswered,
      );
    };
  }, [client, handleInteractiveQuestionAnswered]);

  // The host can turn the ranking off mid-session: anything players already have open
  // (the full ranking, a survey/open-answer summary or the waiting-to-resolve animation)
  // must close with it. Depending on `sidebarSummary.close` — stable — instead of the
  // whole `sidebarSummary` object keeps this to the moment the flag flips, so it never
  // closes the answer feedback that opens later with ranking off.
  useEffect(() => {
    if (rankingEnabled) return;

    setShowWaitingToResolveAnimation(false);
    sidebarSummary.close();
  }, [rankingEnabled, sidebarSummary.close]);

  const handleToggleSidebarOpen = (open?: boolean) => {
    if (open === undefined) {
      sidebarSummary.toggle();
      return;
    }

    if (open) {
      sidebarSummary.open();
      return;
    }

    sidebarSummary.close();
  };

  return (
    <Wrapper>
      <PlaySidebarSummary
        client={client}
        isOpen={sidebarSummary.isOpen}
        toggleOpen={handleToggleSidebarOpen}
        selectedQuestion={sidebarSummary.selectedQuestion?.interactiveQuestion}
        isLoading={showWaitingToResolveAnimation}
        LoadingComponent={<WaitingToResolveAnimation />}
        isLastScoredQuestion={sidebarSummary.selectedQuestion?.isLastScored}
        rankingEnabled={rankingEnabled}
        hideCloseActionButton={
          rankingEnabled && !sidebarSummary.selectedQuestion?.isLastScored
        }
        hideFullscreenActionButton={!sidebarSummary.selectedQuestion?.isLastScored}
        connection={viewConnection.connection}
      />
    </Wrapper>
  );
});
